import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  IconPlus,
  Checkbox,
  IconCopyFilled,
  IconCheck,
  Select,
  Radio,
  IconInfo,
} from 'takeat-design-system-ui-kit';
import toast from 'react-hot-toast';
import api from '~/services/api';
import { useForm } from 'react-hook-form';
import { useAuth } from '~/context/AuthContext';
import { PrinterModal } from '../PrinterModal';
import PrinterRow from './PrinterRow';
import HtmlToPrintArea from '../HtmlToPrintArea';
import HtmlTextPreview from '../HtmlTextPreview';
import {
  ButtonRow,
  CheckboxCard,
  CheckboxItem,
  Container,
  ContainerCheckbox,
  ContainerForm,
  ContainerTypesFonts,
  Divisor,
  FontVisualization,
  Header,
  HeaderWrapper,
  InfoPrinter,
  InputContainer,
  InputField,
  InputLabel,
  MainCard,
  Section,
  SectionText,
  SectionTextWrapper,
  SectionTitle,
  SectionTitleWrapper,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TextTitleTypesFonts,
} from './styles';
import {
  MOCK_PRINTER_VIA_DE_FECHAMENTO,
  MOCK_PRINTER_PRODUÇÃO,
  MOCK_PRINTER_MOTOBOY,
} from './mockPrinters.js';
import { updateFontSizes } from '../utils/updateFontSize';

const NewPrinterPage = ({ user }) => {
  const { updateUser, websocketManager } = useAuth();
  const [isCopying, setIsCopying] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [newPrinterModal, setNewPrinterModal] = useState(false);
  const [printWeb, setPrintWeb] = useState(user.is_printed_on_web);
  const [autoPrint, setAutoPrint] = useState(user?.auto_print_orders);
  const [autoPrintDelivery, setAutoPrintDelivery] = useState(
    user?.auto_print_delivery
  );
  const [isSubmittingPrinter, setIsSubmittingPrinter] = useState(false);
  const {
    register: registerPrint,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    if (user) {
      const initialData = {
        is_printed_on_web: user.is_printed_on_web,
        print_bills_on_web: user.print_bills_on_web,
        print_bills_delivery_on_web: user.print_bills_delivery_on_web,
        print_complement_category_title: user.print_complement_category_title,
        auto_print_orders: user.auto_print_orders,
        auto_print_delivery: user.auto_print_delivery,
        print_baskets_in_kds: user.print_baskets_in_kds,
        print_separate_itens: user.print_separate_itens,
        print_balcony_default: user.print_balcony_default,
        print_canceled: user.print_canceled,
      };

      reset(initialData);
    }
  }, [user, reset]);

  const [typeOptionsPrinter, setTypeOptionsPrinter] = useState([
    {
      label: 'Vias de Fechamento',
      value: 'bill',
      info: 'Vias de delivery e retiradas, Ifood, fechamento PDV e fechamento de conta do delivery.',
      mock: MOCK_PRINTER_VIA_DE_FECHAMENTO,
    },
    {
      label: 'Vias de Produção',
      value: 'kitchen',
      info: 'Delivery, retirada, agendamento, fechamento PDV e comandas individuais.',
      mock: MOCK_PRINTER_PRODUÇÃO,
    },
    {
      label: 'Vias do Motoboy',
      value: 'motoboy',
      info: 'Vias específicas para entregadores motoboy.',
      mock: MOCK_PRINTER_MOTOBOY,
    },
  ]);

  const [typeOptionsPrinterSelected, setTypeOptionsPrinterSelected] = useState({
    label: '',
    value: '',
    info: '',
  });

  const [elementsGerais, setElementsGerais] = useState('');
  const [nameProducts, setNameProducts] = useState('');
  const [nameComplements, setNameComplements] = useState('');
  const [observation, setObservation] = useState('');

  const htmlPreview = updateFontSizes(typeOptionsPrinterSelected.mock || '', {
    elementsGerais,
    nameProducts,
    nameComplements,
    observation,
  });

  const [isUpdatingFont, setIsUpdatingFont] = useState(false);

  const updateFontSetting = async (setter, key, value) => {
    if (isUpdatingFont) return;
    setIsUpdatingFont(true);
    setter(value);
    try {
      await api.put('/restaurants/printer-settings', {
        type: typeOptionsPrinterSelected.value,
        header: key === 'header' ? value : elementsGerais,
        product: key === 'product' ? value : nameProducts,
        complement: key === 'complement' ? value : nameComplements,
        background_observations:
          key === 'background_observations' ? value : observation,
      });
      toast.success('Alteração de fonte feita com sucesso');
    } catch (error) {
      console.log('error', error);
      setter(
        key === 'header'
          ? elementsGerais
          : key === 'product'
          ? nameProducts
          : key === 'complement'
          ? nameComplements
          : observation
      );
      toast.error('Erro ao alterar fonte');
    } finally {
      setIsUpdatingFont(false);
    }
  };

  const getPrinterSettings = useCallback(async () => {
    try {
      const response = await api.get('/restaurants/printer-settings');

      const fontSizesObj =
        response.data[`font_sizes_${typeOptionsPrinterSelected.value}`];

      setElementsGerais(fontSizesObj?.header ?? '');
      setNameProducts(fontSizesObj?.product ?? '');
      setNameComplements(fontSizesObj?.complement ?? '');
      setObservation(fontSizesObj?.background_observations ?? '');

      return response;
    } catch (error) {
      console.log('error settings printer', error);
    }
  }, [typeOptionsPrinterSelected]);

  useEffect(() => {
    getPrinterSettings();
  }, [typeOptionsPrinterSelected]);

  const handlePrinterWebsocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'printer-created':
        setPrinters((prevPrinters) => {
          return [...prevPrinters, data.item];
        });
        toast.success('Nova impressora adicionada!');
        break;
      case 'printer-updated':
        setPrinters((prevPrinters) => {
          return prevPrinters.map((printer) =>
            printer.id === data.item.id ? data.item : printer
          );
        });
        toast.success('Impressora atualizada!');
        break;
      case 'printer-deleted':
        setPrinters((prevPrinters) => {
          return prevPrinters.filter((printer) => printer.id !== data.item.id);
        });
        toast.success('Impressora removida!');
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (websocketManager) {
      websocketManager.addMessageCallback(
        'newPrinterPage',
        handlePrinterWebsocketMessage
      );
    }

    return () => {
      if (websocketManager) {
        websocketManager.removeMessageCallback('newPrinterPage');
      }
    };
  }, [websocketManager, handlePrinterWebsocketMessage]);

  function changePrintWeb() {
    setPrintWeb(!printWeb);
  }
  function handleChangeAutoPrint() {
    setAutoPrint(!autoPrint);
  }
  function handleChangeAutoPrintDelivery() {
    setAutoPrintDelivery(!autoPrintDelivery);
  }
  const handleSubmitPrinter = async (data) => {
    setIsSubmittingPrinter(true);
    try {
      let updateProfile = null;

      await api.put('/restaurants/settings', {
        print_complement_category_title: data.print_complement_category_title,
      });
      if (data.is_printed_on_web) {
        updateProfile = {
          is_printed_on_web: data.is_printed_on_web,
          auto_print_orders: false,
          print_bills_on_web: false,
          print_separate_itens: false,
          print_transfer: false,
          print_canceled: false,
          auto_print_delivery: false,
          print_bills_delivery_on_web: false,
          // ifood_auto_accept: false,
          email: user.email,
          print_balcony_default: false,
          print_complement_category_title: data.print_complement_category_title,
          print_baskets_in_kds: false,
        };
      } else {
        updateProfile = {
          is_printed_on_web: data.is_printed_on_web,
          auto_print_orders: data.auto_print_orders,
          print_bills_on_web: data.print_bills_on_web,
          print_separate_itens: data.print_separate_itens,
          print_transfer: data.print_transfer,
          print_canceled: data.print_canceled,
          auto_print_delivery: data.auto_print_delivery,
          print_bills_delivery_on_web: data.print_bills_delivery_on_web,
          // ifood_auto_accept: data.ifood_auto_accept,
          email: user.email,
          print_balcony_default: data.print_balcony_default,
          print_complement_category_title: data.print_complement_category_title,
          print_baskets_in_kds: data.print_baskets_in_kds,
        };
      }
      updateUser(updateProfile);
    } catch (err) {
      toast.error('Falha ao atualizar.');
    } finally {
      setIsSubmittingPrinter(false);
    }
  };
  const openNewPrinterModal = async () => {
    setNewPrinterModal(true);
  };
  const handleModalClose = () => {
    setNewPrinterModal(false);
  };
  const downloadPrinterApp = () => {
    if (!downloadUrl) {
      toast.error('URL para download do app não disponível.');
      return;
    }
    window.open(downloadUrl);
  };
  useEffect(() => {
    const fetchPrinterApp = async () => {
      try {
        const response = await fetch(
          'https://api.github.com/repos/TakeatGD/takeat-printer-app/releases/latest'
        );
        const data = await response.json();
        const exeAsset = data.assets.find((asset) =>
          asset.name.endsWith('.exe')
        );
        if (exeAsset) {
          setDownloadUrl(exeAsset.browser_download_url);
        }
      } catch (e) {
        console.log('error fetching printer-app url:', e);
      }
    };
    fetchPrinterApp();
  }, []);
  const handleDownloadApp = () => {
    downloadPrinterApp();
  };
  const getPrinters = async () => {
    try {
      const response = await api.get('restaurants/printers');
      setPrinters(response.data);
    } catch (error) {
      toast.error('Erro ao buscar impressoras');
      console.error('Error fetching printers:', error);
    }
  };
  useEffect(() => {
    getPrinters();
  }, []);
  const handleCopyKey = async () => {
    if (user?.printer_token && !isCopying) {
      setIsCopying(true);
      try {
        await navigator.clipboard.writeText(user.printer_token);
        toast.success('Chave copiada com sucesso!');
      } catch (error) {
        toast.error('Erro ao copiar a chave');
      }
      setTimeout(() => {
        setIsCopying(false);
      }, 2000);
    }
  };
  const handleDeletePrinter = (printerId) => {
    setPrinters((prevPrinters) =>
      prevPrinters.filter((printer) => printer.id !== printerId)
    );
  };
  const handleUpdatePrinter = (updatedPrinter) => {
    setPrinters((prevPrinters) =>
      prevPrinters.map((printer) =>
        printer.id === updatedPrinter.id ? updatedPrinter : printer
      )
    );
  };
  return (
    <Container>
      <HeaderWrapper>
        <Header>Dispositivos e impressoras</Header>
      </HeaderWrapper>
      <MainCard>
        <Section>
          <SectionTitleWrapper>
            <SectionTitle>
              Integração com aplicativo de impressão automática
            </SectionTitle>
          </SectionTitleWrapper>
          <SectionTextWrapper>
            <SectionText>
              O aplicativo de impressão Takeat é um forma de facilitar a
              impressão dos pedidos, fechamento de conta e de vias de delivery
              de forma automatizada. Siga o passo a passo abaixo para iniciar o
              uso eficiente do aplicativo:
              <br />
              Clique em &quot;Baixar aplicativo&quot; instale o aplicativo de
              impressão no computador.
              <br />
              Copie a chave de acesso clicando em &quot;Copiar chave&quot;.
              <br />
              Volte ao aplicativo e cole a chave para vincular à sua conta.
              <br />
              Pronto! A impressão automática estará disponível para ser usada.
            </SectionText>
          </SectionTextWrapper>
          <ButtonRow>
            <Button onClick={handleDownloadApp}>
              <IconPlus />
              Baixar aplicativo
            </Button>
            <InputContainer>
              <InputLabel>Chave de acesso</InputLabel>
              <InputField>
                {user?.printer_token || 'Chave não disponível'}
              </InputField>
            </InputContainer>
            <Button
              variant="outlined"
              onClick={handleCopyKey}
              disabled={!user?.printer_token || isCopying}
              style={{ backgroundColor: '#fff' }}
            >
              {isCopying ? <IconCheck /> : <IconCopyFilled />}
              {isCopying ? 'Chave copiada' : 'Copiar chave'}
            </Button>
          </ButtonRow>
        </Section>
        <form onSubmit={handleSubmit(handleSubmitPrinter)}>
          <ContainerForm>
            <Section>
              <SectionTitleWrapper>
                <SectionTitle>Impressão manual</SectionTitle>
              </SectionTitleWrapper>
              <CheckboxCard>
                <ContainerCheckbox>
                  <Checkbox
                    label="Impressão via web"
                    name="print_web"
                    register={registerPrint('is_printed_on_web')}
                    onClick={changePrintWeb}
                  />
                  <InfoPrinter>
                    Ao deixar essa opção selecionada, todas as impressões serão
                    feitas pelo navegador. Se desmarcada, é necessário ter o
                    programa de impressão da Takeat instalado e com sua(s)
                    impressora(s) configurada(s) no seu computador.
                  </InfoPrinter>
                </ContainerCheckbox>
                {printWeb ? (
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão de Fechamento de Comanda e Relatórios via Web"
                      name="print_bills_on_web"
                      disabled
                      checked={false}
                    />
                    <InfoPrinter>
                      Ao ativar a Impressão de Fechamento de Comanda via Web, as
                      comandas serão impressas via web mesmo com o programa da
                      Takeat instalado no seu computador.
                    </InfoPrinter>
                  </ContainerCheckbox>
                ) : (
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão de Fechamento de Comanda e Relatórios via Web"
                      name="print_bills_on_web"
                      register={registerPrint('print_bills_on_web')}
                    />
                    <InfoPrinter>
                      Ao ativar a Impressão de Fechamento de Comanda via Web, as
                      comandas serão impressas via web mesmo com o programa da
                      Takeat instalado no seu computador.
                    </InfoPrinter>
                  </ContainerCheckbox>
                )}
                {printWeb ? (
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão de via de Entrega via Web"
                      name="print_bills_delivery_on_web"
                      register={registerPrint('print_bills_delivery_on_web')}
                      disabled
                      checked={false}
                    />
                    <InfoPrinter>
                      Ao ativar a Impressão de via de Entrega via Web, as
                      comandas com informações de entrega do delivery serão
                      impressas via web mesmo com o programa da Takeat instalado
                      no seu computador.
                    </InfoPrinter>
                  </ContainerCheckbox>
                ) : (
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão de via de Entrega via Web"
                      name="print_bills_delivery_on_web"
                      register={registerPrint('print_bills_delivery_on_web')}
                    />
                    <InfoPrinter>
                      Ao ativar a Impressão de via de Entrega via Web, as
                      comandas com informações de entrega do delivery serão
                      impressas via web mesmo com o programa da Takeat instalado
                      no seu computador.
                    </InfoPrinter>
                  </ContainerCheckbox>
                )}
              </CheckboxCard>
            </Section>
            <Section>
              <SectionTitleWrapper>
                <SectionTitle>Impressão automática</SectionTitle>
                <InfoPrinter $infoAutomatic>
                  Se a impressão for pelo programa de impressão da Takeat,
                  instalado no seu computador, é possível que os pedidos sejam
                  aceitos e impressos automaticamentes ao serem feitos. Com essa
                  opção selecionada, todos os pedidos serão aceitos
                  automaticamente e vão direto para a aba "Em Preparo" na área
                  de acompanhar os pedidos.
                </InfoPrinter>
              </SectionTitleWrapper>
              {printWeb ? (
                <CheckboxCard>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Pedidos"
                      name="auto_print_orders"
                      disabled
                      checked={false}
                    />
                  </ContainerCheckbox>
                  {user?.delivery_info?.is_delivery_allowed && (
                    <ContainerCheckbox>
                      <Checkbox
                        label="Impressão Automática de Pedidos de Delivery"
                        name="auto_print_delivery"
                        disabled
                        checked={false}
                      />
                    </ContainerCheckbox>
                  )}
                  <ContainerCheckbox>
                    <Checkbox
                      label="Imprimir itens separadamente"
                      name="print_separate_itens"
                      disabled
                      checked={false}
                    />
                  </ContainerCheckbox>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Comanda de Balcão"
                      name="print_balcony_default"
                      disabled
                      checked={false}
                    />
                  </ContainerCheckbox>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Itens Cancelados"
                      name="print_canceled"
                      disabled
                      checked={false}
                    />
                  </ContainerCheckbox>
                </CheckboxCard>
              ) : (
                <CheckboxCard>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Pedidos"
                      name="auto_print_orders"
                      onClick={handleChangeAutoPrint}
                      register={registerPrint('auto_print_orders')}
                    />
                  </ContainerCheckbox>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Pedidos de Delivery"
                      name="auto_print_delivery"
                      onClick={handleChangeAutoPrintDelivery}
                      register={registerPrint('auto_print_delivery')}
                    />
                  </ContainerCheckbox>
                  {(autoPrintDelivery || autoPrint) && (
                    <ContainerCheckbox>
                      <Checkbox
                        label="Impressão pelo KDS"
                        name="print_baskets_in_kds"
                        register={registerPrint('print_baskets_in_kds')}
                      />
                    </ContainerCheckbox>
                  )}
                  <ContainerCheckbox>
                    <Checkbox
                      label="Imprimir itens separadamente"
                      name="print_separate_itens"
                      register={registerPrint('print_separate_itens')}
                    />
                  </ContainerCheckbox>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Comanda de Balcão"
                      name="print_balcony_default"
                      register={registerPrint('print_balcony_default')}
                    />
                  </ContainerCheckbox>
                  <ContainerCheckbox>
                    <Checkbox
                      label="Impressão Automática de Itens Cancelados"
                      name="print_canceled"
                      register={registerPrint('print_canceled')}
                    />
                  </ContainerCheckbox>
                </CheckboxCard>
              )}
            </Section>
            <Section>
              <SectionTitleWrapper>
                <SectionTitle>
                  Detalhamento nas vias de fechamento e produção
                </SectionTitle>
                <InfoPrinter>
                  Ao ativar a opção abaixo, permite que seja impresso o título
                  da categoria de complemento.
                </InfoPrinter>
              </SectionTitleWrapper>
              <CheckboxCard>
                <ContainerCheckbox>
                  <Checkbox
                    label="Imprimir título da categoria de complemento"
                    name="print_complement_category_title"
                    register={registerPrint('print_complement_category_title')}
                  />
                </ContainerCheckbox>
              </CheckboxCard>
            </Section>

            <Button
              type="submit"
              isLoading={isSubmittingPrinter}
              disabled={isSubmittingPrinter}
            >
              Salvar
            </Button>
          </ContainerForm>
        </form>
        <Section>
          <SectionTitle>Tamanho de exibição na folha de impressão</SectionTitle>
          <div style={{ display: 'flex', gap: '16px' }}>
            <CheckboxCard>
              <Select
                label="Tipo de Impressão"
                info={
                  typeOptionsPrinterSelected.info ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 4,
                      }}
                    >
                      <IconInfo /> {typeOptionsPrinterSelected.info}
                    </div>
                  ) : null
                }
                options={typeOptionsPrinter}
                onChange={(e) => {
                  setTypeOptionsPrinterSelected(e);
                  console.log(e);
                }}
              />
              {typeOptionsPrinterSelected.value && (
                <>
                  <ContainerTypesFonts>
                    <TextTitleTypesFonts>
                      Elementos gerais de impressão
                    </TextTitleTypesFonts>
                    <Radio
                      label="Texto pequeno"
                      value={0}
                      checked={elementsGerais === 0}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setElementsGerais, 'header', 0);
                      }}
                    />
                    <Radio
                      label="Texto médio"
                      value={4}
                      checked={elementsGerais === 4}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setElementsGerais, 'header', 4);
                      }}
                    />
                    <Radio
                      label="Texto grande"
                      value={8}
                      checked={elementsGerais === 8}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setElementsGerais, 'header', 8);
                      }}
                    />
                  </ContainerTypesFonts>
                  <ContainerTypesFonts>
                    <TextTitleTypesFonts>Nome dos produtos</TextTitleTypesFonts>
                    <Radio
                      label="Texto pequeno"
                      value={0}
                      checked={nameProducts === 0}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setNameProducts, 'product', 0);
                      }}
                    />
                    <Radio
                      label="Texto médio"
                      value={4}
                      checked={nameProducts === 4}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setNameProducts, 'product', 4);
                      }}
                    />
                    <Radio
                      label="Texto grande"
                      value={8}
                      checked={nameProducts === 8}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setNameProducts, 'product', 8);
                      }}
                    />
                  </ContainerTypesFonts>
                  <ContainerTypesFonts>
                    <TextTitleTypesFonts>
                      Nome dos complementos
                    </TextTitleTypesFonts>
                    <Radio
                      label="Texto pequeno"
                      value={0}
                      checked={nameComplements === 0}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setNameComplements, 'complement', 0);
                      }}
                    />
                    <Radio
                      label="Texto médio"
                      value={4}
                      checked={nameComplements === 4}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setNameComplements, 'complement', 4);
                      }}
                    />
                    <Radio
                      label="Texto grande"
                      value={8}
                      checked={nameComplements === 8}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(setNameComplements, 'complement', 8);
                      }}
                    />
                  </ContainerTypesFonts>
                  <ContainerTypesFonts>
                    <TextTitleTypesFonts>Observação</TextTitleTypesFonts>
                    <Radio
                      label="Texto padrão"
                      value
                      checked={observation === true}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(
                          setObservation,
                          'background_observations',
                          true
                        );
                      }}
                    />
                    <Radio
                      label="Texto sem destaque"
                      value={false}
                      checked={observation === false}
                      disabled={isUpdatingFont}
                      onChange={() => {
                        updateFontSetting(
                          setObservation,
                          'background_observations',
                          false
                        );
                      }}
                    />
                  </ContainerTypesFonts>
                </>
              )}
            </CheckboxCard>

            <CheckboxCard>
              <SectionTitle>Pré-visualização</SectionTitle>
              {typeOptionsPrinterSelected.value === '' ? (
                <FontVisualization>
                  <InfoPrinter>
                    Selecione o tipo de impressão para poder visualizá-lo.
                  </InfoPrinter>
                </FontVisualization>
              ) : (
                <HtmlTextPreview html={htmlPreview} foodies={false} />
              )}
            </CheckboxCard>
          </div>
        </Section>
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <SectionTitle>Impressoras cadastradas</SectionTitle>
            <Button style={{ height: 32 }} onClick={openNewPrinterModal}>
              <IconPlus />
              Adicionar impressora
            </Button>
          </div>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Nome Takeat</TableHeaderCell>
                  <TableHeaderCell>Nome no Computador</TableHeaderCell>
                  <TableHeaderCell>Imprime</TableHeaderCell>
                  <TableHeaderCell>Todos os produtos</TableHeaderCell>
                  <TableHeaderCell>Ações</TableHeaderCell>
                </tr>
              </TableHeader>
              <tbody>
                {printers.map((printer) => (
                  <PrinterRow
                    key={printer.id}
                    printer={printer}
                    onDelete={handleDeletePrinter}
                    onUpdate={handleUpdatePrinter}
                  />
                ))}
                {printers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      Nenhuma impressora cadastrada
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>
        </div>
      </MainCard>
      {user.id === 2 && (
        <>
          <MainCard style={{ marginTop: 24 }}>
            <HtmlToPrintArea />
          </MainCard>
          <MainCard style={{ marginTop: 24 }}>
            <HtmlTextPreview />
          </MainCard>
        </>
      )}
      {newPrinterModal && (
        <PrinterModal
          open={newPrinterModal}
          onClose={handleModalClose}
          isEdit={false}
          printerData={null}
          onPrinterCreate={getPrinters}
        />
      )}
    </Container>
  );
};

export default NewPrinterPage;
